import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LivePrice } from '../types';
import { api } from '../api';
import './LivePrices.css';

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);

const formatPrice = (val: number) => {
  if (val >= 1000) return formatCurrency(val);
  if (val >= 1) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(6)}`;
};

interface LivePricesProps {
  refreshTrigger?: number;
}

const LivePrices: React.FC<LivePricesProps> = ({ refreshTrigger }) => {
  const { t } = useTranslation();
  const [prices, setPrices] = useState<LivePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPrices = async () => {
    try {
      const data = await api.getLivePrices();
      setPrices(data);
    } catch (err) {
      console.error('Failed to fetch live prices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();
    intervalRef.current = setInterval(fetchPrices, 3000); // refresh every 3s
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Re-fetch when a trade opens/closes
  useEffect(() => {
    if (refreshTrigger) fetchPrices();
  }, [refreshTrigger]);

  if (loading) {
    return <div style={{ color: '#a1a1aa', padding: '1rem' }}>{t('livePrices.loading')}</div>;
  }

  if (prices.length === 0) {
    return null;
  }

  return (
    <div className="live-prices-wrap">
      <div className="live-prices-header">
        <h3>{t('livePrices.title')}</h3>
        <div className="live-prices-badge">
          <span className="live-dot-pulse" />
          {t('livePrices.autoRefresh')}
        </div>
      </div>
      <div className="live-prices-grid">
        {prices.map(p => {
          const pnlClass = (p.unrealizedPnl ?? 0) >= 0 ? 'positive' : 'negative';
          const pnlSign = (p.unrealizedPnl ?? 0) >= 0 ? '+' : '';

          return (
            <div key={p.tradeId} className="live-price-card">
              <div className="live-price-top">
                <div className="live-price-pair">
                  <span className="live-price-symbol">{p.pair}</span>
                  <span className={`live-price-type live-price-type--${p.type.toLowerCase()}`}>
                    {p.type} {p.leverage}x
                  </span>
                </div>
                <div className={`live-price-pnl ${pnlClass}`}>
                  {p.unrealizedPnl !== null ? (
                    <>
                      <span className="live-price-pnl-value">{pnlSign}{formatCurrency(p.unrealizedPnl)}</span>
                      <span className="live-price-pnl-pct">{pnlSign}{p.unrealizedPnlPct?.toFixed(2)}%</span>
                    </>
                  ) : (
                    <span style={{ color: '#a1a1aa' }}>—</span>
                  )}
                </div>
              </div>
              <div className="live-price-bottom">
                <div className="live-price-detail">
                  <span className="live-price-label">{t('livePrices.entry')}</span>
                  <span className="live-price-value">{formatPrice(p.entryPrice)}</span>
                </div>
                <div className="live-price-detail">
                  <span className="live-price-label">{t('livePrices.current')}</span>
                  <span className="live-price-value">
                    {p.currentPrice !== null ? formatPrice(p.currentPrice) : '—'}
                  </span>
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LivePrices;
